// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import {
  registerUser,
  loginUser,
  logoutUser,
  getUser,
  getCachedUser,
  setCurrentUser,
  clearCachedUser,
} from "../utils/api";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);       // for bootstrap / fetchUser
  const [authLoading, setAuthLoading] = useState(false); // for login/register

  // -------- bootstrap with cached user immediately --------
  useEffect(() => {
    const localUser = getCachedUser();
    if (localUser) {
      setUser(localUser);
      setCurrentUser(localUser); // keep cache + memory consistent
    }
  }, []);

  // -------- fetchUser (accepts skip flag for startup) --------
  const fetchUser = async (skipAuthErrorHandling = false) => {
  setLoading(true);
  try {
    const u = await getUser(false, skipAuthErrorHandling); // use improved getUser
    setUser(u);
    setCurrentUser(u);
    return u;  // 🔹 return fetched user
  } catch (err) {
    setUser(null);
    setCurrentUser(null); // clear cache + memory
    return null;
  } finally {
    setLoading(false);
  }
};
  

  // -------- bootstrap once (avoids double-run in React 18 StrictMode) --------
  const bootstrapped = useRef(false);
  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    const localUser = getCachedUser();
    if (localUser) setUser(localUser); // optimistic hydrate

    // Don’t ping backend while on auth routes
    const onAuthRoute = /^\/(login|register|verify-email|forgot-password)/.test(
      window.location.pathname
    );
    if (!onAuthRoute) {
      fetchUser(true); // skip global 401 handling on boot
    }
  }, []);

  // -------- Auth actions --------
  const register = async (formData) => {
    setAuthLoading(true);
    try {
      await registerUser(formData); // will setCurrentUser internally
      await fetchUser(); // enforce normal handling
    } finally {
      setAuthLoading(false);
    }
  };
  
  const login = async (email, password) => {
  setAuthLoading(true);
  try {
    await loginUser(email, password);     // sets session/cookie
    const u = await fetchUser();          // fetch logged-in user
    return u;                             // 🔹 return user so caller can redirect
  } finally {
    setAuthLoading(false);
  }
};

  const logout = async () => {
    try {
      await logoutUser();
    } finally {
      setUser(null);
      clearCachedUser(); // clear local cache
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        authLoading,
        login,
        register,
        logout,
          setUser, 
        fetchUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);